import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Card,
  List,
  Tag,
  Typography,
  Space,
  Avatar,
  Empty,
  Spin,
  Tabs,
  Button,
} from 'antd';
import {
  FileTextOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import api from '../config/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState('all');

  const { data: searchResults, isLoading } = useQuery(
    ['global-search', query],
    () => {
      if (!query || query.trim().length === 0) {
        return { tickets: [], businesses: [], users: [], total: 0 };
      }
      return api.get('/api/search', {
        params: { q: query, limit: 20 }
      }).then(res => res.data);
    },
    {
      enabled: !!query && query.trim().length > 0,
      retry: false
    }
  );

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'default',
      open: 'blue',
      pending: 'orange',
      solved: 'green',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'blue',
      medium: 'orange',
      high: 'red',
      urgent: 'red',
    };
    return colors[priority] || 'default';
  };

  const tickets = searchResults?.tickets || [];
  const businesses = searchResults?.businesses || [];
  const users = searchResults?.users || [];
  const total = searchResults?.total || 0;

  const tabItems = [
    {
      key: 'all',
      label: `All (${total})`,
    },
    {
      key: 'tickets',
      label: `Tickets (${tickets.length})`,
    },
    {
      key: 'businesses',
      label: `Businesses (${businesses.length})`,
    },
    ...(users.length > 0 ? [{
      key: 'users',
      label: `Users (${users.length})`,
    }] : []),
  ];

  const renderContent = () => {
    if (activeTab === 'tickets') {
      if (tickets.length === 0) {
        return (
          <Empty
            description="No tickets found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={tickets}
          renderItem={(ticket: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                title={
                  <Space>
                    <Text strong>#{ticket.ticket_number}</Text>
                    <Text>{ticket.subject}</Text>
                  </Space>
                }
                description={
                  <Space>
                    <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                    <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                    {ticket.business_name && <Text type="secondary">{ticket.business_name}</Text>}
                    {ticket.assigned_to_name && (
                      <Text type="secondary">
                        Assigned to: {ticket.assigned_to_name} {ticket.assigned_to_last_name || ''}
                      </Text>
                    )}
                    <Text type="secondary">{dayjs(ticket.created_at).fromNow()}</Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    if (activeTab === 'businesses') {
      if (businesses.length === 0) {
        return (
          <Empty
            description="No businesses found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={businesses}
          renderItem={(business: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/businesses/${business.id}`)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                title={<Text strong>{business.business_name}</Text>}
                description={
                  <Space direction="vertical" size="small">
                    {business.mid && <Text type="secondary">MID: {business.mid}</Text>}
                    {business.email && <Text type="secondary">Email: {business.email}</Text>}
                    {business.phone && <Text type="secondary">Phone: {business.phone}</Text>}
                    {business.address && (
                      <Text type="secondary">
                        {business.address}
                        {business.city && `, ${business.city}`}
                        {business.state && `, ${business.state}`}
                        {business.zip_code && ` ${business.zip_code}`}
                      </Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    if (activeTab === 'users') {
      if (users.length === 0) {
        return (
          <Empty
            description="No users found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={users}
          renderItem={(user: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/users`)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }}>
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </Avatar>
                }
                title={
                  <Space>
                    <Text strong>{user.first_name} {user.last_name}</Text>
                    {user.role_display_name && (
                      <Tag>{user.role_display_name}</Tag>
                    )}
                    {!user.is_active && <Tag color="red">Inactive</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    {user.email && <Text type="secondary">Email: {user.email}</Text>}
                    {user.phone && <Text type="secondary">Phone: {user.phone}</Text>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    // All tab
    if (total === 0) {
      return (
        <Empty
          description={query ? "No results found" : "Enter a search term to begin"}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {tickets.length > 0 && (
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <Text strong>Tickets ({tickets.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={tickets.slice(0, 5)}
              renderItem={(ticket: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                    title={
                      <Space>
                        <Text strong>#{ticket.ticket_number}</Text>
                        <Text>{ticket.subject}</Text>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                        <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                        {ticket.business_name && <Text type="secondary">{ticket.business_name}</Text>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {tickets.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => setActiveTab('tickets')}>
                  View all {tickets.length} tickets
                </Button>
              </div>
            )}
          </Card>
        )}

        {businesses.length > 0 && (
          <Card
            title={
              <Space>
                <ShopOutlined />
                <Text strong>Businesses ({businesses.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={businesses.slice(0, 5)}
              renderItem={(business: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/businesses/${business.id}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                    title={<Text strong>{business.business_name}</Text>}
                    description={
                      <Space direction="vertical" size="small">
                        {business.mid && <Text type="secondary">MID: {business.mid}</Text>}
                        {business.email && <Text type="secondary">Email: {business.email}</Text>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {businesses.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => setActiveTab('businesses')}>
                  View all {businesses.length} businesses
                </Button>
              </div>
            )}
          </Card>
        )}

        {users.length > 0 && (
          <Card
            title={
              <Space>
                <UserOutlined />
                <Text strong>Users ({users.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={users.slice(0, 5)}
              renderItem={(user: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/users`)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>{user.first_name} {user.last_name}</Text>
                        {user.role_display_name && <Tag>{user.role_display_name}</Tag>}
                      </Space>
                    }
                    description={
                      <Text type="secondary">{user.email}</Text>
                    }
                  />
                </List.Item>
              )}
            />
            {users.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => setActiveTab('users')}>
                  View all {users.length} users
                </Button>
              </div>
            )}
          </Card>
        )}
      </Space>
    );
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {query && (
            <>
              <div>
                <Text type="secondary">
                  {isLoading ? 'Searching...' : `Found ${total} result${total !== 1 ? 's' : ''} for "${query}"`}
                </Text>
              </div>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {total > 0 && (
                    <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      items={tabItems}
                    />
                  )}
                  {renderContent()}
                </>
              )}
            </>
          )}

          {!query && (
            <Empty
              description="Enter a search term in the header to find tickets, businesses, or users"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default SearchResults;

